/**
 * DashboardPage - Página principal del dashboard con estadísticas y gráficos
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Container,
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp,
  AccountBalance,
  Warning,
  EventAvailable,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { PageHeader, LoadingSpinner } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import * as reporteService from '../services/reporteService';
import { parseApiDate } from '../utils/formatters';

// Colores para gráficos
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const DashboardPage = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [postDatedChecks, setPostDatedChecks] = useState([]);

  // Cargar datos del dashboard
  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Obtener estadísticas generales
      const statsData = await reporteService.getGeneralStats();
      setStats(statsData);

      // Obtener cheques post-fechados próximos a vencer
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);

      const calendar = await reporteService.getPostDatedCalendar(
        nextMonth.getMonth() + 1,
        nextMonth.getFullYear()
      );

      // Filtrar cheques próximos a vencer (próximos 7 días)
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const upcoming = calendar.cheques?.filter((cheque) => {
        const checkDate = parseApiDate(cheque.fecha_pago);
        return checkDate && checkDate >= today && checkDate <= nextWeek;
      }) || [];

      setPostDatedChecks(upcoming);
    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
      showError('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();

    // Actualizar cada 30 segundos (opcional)
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Formatear una fecha del API (DATEONLY) sin desfase de zona horaria
  const formatFecha = (fecha) => {
    const parsed = parseApiDate(fecha);
    return parsed ? parsed.toLocaleDateString('es-AR') : '-';
  };

  if (loading) {
    return <LoadingSpinner message="Cargando dashboard..." />;
  }

  if (!stats) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Error al cargar datos del dashboard</Typography>
      </Box>
    );
  }

  // Preparar datos para gráficos
  const estadoData = [
    { name: 'Pendientes', value: stats.chequesPorEstado?.pendiente || 0 },
    { name: 'Depositados', value: stats.chequesPorEstado?.depositado || 0 },
    { name: 'Con Novedad', value: stats.chequesPorEstado?.novedad || 0 },
    { name: 'Rechazados', value: stats.chequesPorEstado?.rechazado || 0 },
  ];

  const bancoData = stats.chequesPorBanco?.map((item) => ({
    name: item.banco || 'Sin banco',
    value: item.cantidad || 0,
  })) || [];

  const depositosData = stats.depositosPorMes?.map((item) => ({
    name: `${item.mes}/${item.anio}`,
    depositos: item.cantidad || 0,
    monto: item.monto || 0,
  })) || [];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Dashboard"
        breadcrumbs={[]}
        actions={[
          // Solo mostrar "Nuevo Depósito" si NO es cajero
          ...(user?.rol !== 'cajero'
            ? [
                {
                  label: 'Nuevo Depósito',
                  icon: AddIcon,
                  onClick: () => navigate('/depositos/nuevo'),
                  variant: 'outlined',
                },
              ]
            : []),
          {
            label: 'Nuevo Cheque',
            icon: AddIcon,
            onClick: () => navigate('/cheques/nuevo'),
            variant: 'contained',
          },
        ]}
      />

      {/* Tarjetas de estadísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Cheques Pendientes */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'warning.light',
                    color: 'warning.dark',
                    p: 1,
                    borderRadius: 1,
                    mr: 2,
                  }}
                >
                  <EventAvailable />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Pendientes
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.chequesPorEstado?.pendiente || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Intl.NumberFormat('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                }).format(stats.montosPorEstado?.pendiente || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Cheques Depositados */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'success.light',
                    color: 'success.dark',
                    p: 1,
                    borderRadius: 1,
                    mr: 2,
                  }}
                >
                  <TrendingUp />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Depositados
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.chequesPorEstado?.depositado || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Intl.NumberFormat('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                }).format(stats.montosPorEstado?.depositado || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Cheques con Novedad */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'error.light',
                    color: 'error.dark',
                    p: 1,
                    borderRadius: 1,
                    mr: 2,
                  }}
                >
                  <Warning />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Con Novedad
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.chequesPorEstado?.novedad || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Requieren atención
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Cheques Post-fechados */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'info.light',
                    color: 'info.dark',
                    p: 1,
                    borderRadius: 1,
                    mr: 2,
                  }}
                >
                  <AccountBalance />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Próximos a Vencer
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {postDatedChecks.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Próximos 7 días
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Gráfico de Barras - Cheques por Estado */}
        <Grid item xs={12} md={6} lg={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Cheques por Estado
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={estadoData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#8884d8">
                  {estadoData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfico de Torta - Cheques por Banco */}
        <Grid item xs={12} md={6} lg={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Cheques por Banco
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={bancoData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {bancoData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfico de Línea - Depósitos del último mes - Solo para NO cajeros */}
        {user?.rol !== 'cajero' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Depósitos Mensuales (Últimos 6 Meses)
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={depositosData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="depositos"
                    stroke="#8884d8"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Lista de cheques post-fechados próximos a vencer */}
      {postDatedChecks.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 2 }}>
            Cheques Post-fechados Próximos a Vencer (7 días)
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Fecha Cobro</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {postDatedChecks.slice(0, 5).map((cheque) => (
                  <TableRow key={cheque._id} hover>
                    <TableCell>{cheque.numero_cheque}</TableCell>
                    <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                    <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', {
                        style: 'currency',
                        currency: 'ARS',
                      }).format(cheque.monto)}
                    </TableCell>
                    <TableCell>
                      {formatFecha(cheque.fecha_pago)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cheque.estado}
                        color="warning"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {postDatedChecks.length > 5 && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/cheques?estado=pendiente')}
              >
                Ver todos los cheques pendientes
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default DashboardPage;
